import { Injectable, InjectionToken, inject } from '@angular/core';
import { OktaAuth, SignoutOptions } from '@okta/okta-auth-js';
import { WindowService } from '@drc-eca/eca-components-lib';
import { environment } from '../../../environments/environment';
export const OKTA_AUTH_CLIENT = new InjectionToken<any>('OKTA_AUTH_CLIENT');

@Injectable({
  providedIn: 'root'
})
export class OktaService {
  private oktaAuthClient = inject<OktaAuth>(OKTA_AUTH_CLIENT);
  private windowService = inject(WindowService);

  private getOktaOrgUrl(): string {
    const issuer = environment.okta.clientConfig.issuer;
    return new URL(issuer).origin;
  }

  private async getMyAccountOrgUrl(): Promise<string> {
    const fallbackOrgUrl = this.getOktaOrgUrl();

    try {
      const tokens = await this.oktaAuthClient.tokenManager.getTokens();
      const issuer = tokens?.idToken?.issuer || tokens?.accessToken?.claims?.iss;

      if (issuer) {
        return new URL(issuer).origin;
      }
    } catch (e) {
      console.debug('Could not determine issuer from active tokens, using configured origin', e);
    }

    return fallbackOrgUrl;
  }

  isUserLoggedIn(): Promise<boolean> {
    return this.oktaAuthClient.session.exists();
  }

  signIn(credentials: any) {
    return this.oktaAuthClient.signIn(credentials);
  }

  setCookieAndRedirect(sessionToken: string, redirectUrl?: string) {
    if (redirectUrl) {
      return this.oktaAuthClient.session.setCookieAndRedirect(sessionToken, redirectUrl);
    }
    return this.oktaAuthClient.session.setCookieAndRedirect(sessionToken);
  }

  sessionExists() {
    return this.oktaAuthClient.session.exists();
  }

  getTokens(scopes: string[]) {
    return this.oktaAuthClient.token.getWithoutPrompt({ scopes });
  }

  private async getMyAccountAccessToken(scopes: string[]): Promise<string | void> {
    // Prefer already cached tokens; this avoids prompt=none OAuth errors when there is no active session.
    try {
      const existingTokens = await this.oktaAuthClient.tokenManager.getTokens();
      const cachedAccessToken = existingTokens?.accessToken?.accessToken;
      if (cachedAccessToken) {
        return cachedAccessToken;
      }
    } catch (e) {
      console.debug('Could not read cached tokens from tokenManager, trying promptless token retrieval', e);
    }

    try {
      const tokens = await this.getTokens(scopes);
      return tokens.tokens.accessToken?.accessToken;
    } catch (tokenErr: any) {
      const errorText = `${tokenErr?.name || ''} ${tokenErr?.message || ''}`;
      const isNotLoggedInPromptNone = errorText.includes('client specified not to prompt') || errorText.includes('not logged in');

      if (isNotLoggedInPromptNone) {
        console.debug('No promptless Okta token available; continuing with session-only request');
      } else {
        console.debug('Could not obtain access token, trying endpoint with session only:', tokenErr);
      }
    }

    return;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<any> {
    const baseUrl = await this.getMyAccountOrgUrl();
    const accessToken = await this.getMyAccountAccessToken(environment.okta.clientConfig.scopes);

    const headers: any = {
      'Accept': 'application/json; okta-version=1.0.0',
      'Content-Type': 'application/json'
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${baseUrl}/idp/myaccount/password/change-password`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        oldPassword: currentPassword,
        newPassword: newPassword
      })
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw {
        status: response.status,
        ...payload
      };
    }

    return payload;
  }

  async getPasswordComplexityRequirements(): Promise<any> {
    const orgUrl = await this.getMyAccountOrgUrl();

    // Try multiple endpoint strategies
    const endpoint = `${orgUrl}/idp/myaccount/password/complexity-requirements`;

    let firstError = null;
    let accessToken: string | void;

    try {
      accessToken = await this.getMyAccountAccessToken(environment.okta.clientConfig.scopes);
      if (accessToken) {
        console.debug('Obtained access token for Okta API');
      }

      const headers: any = {
        'Accept': 'application/json; okta-version=1.0.0'
      };

      // Add Authorization header if we have an access token
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(endpoint, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers
      });

      const payload = await response.json().catch(() => ({}));

      if (response.ok) {
        console.debug(`Okta complexity requirements retrieved from ${endpoint}`);
        return payload;
      }

      // Capture first error for potential fallback
      const error = {
        status: response.status,
        endpoint: endpoint,
        isCorsError: response.status === 0 || response.type === 'opaque',
        ...payload
      };

      if (!firstError) {
        firstError = error;
      }

      // 401 typically means invalid/missing session or auth; try next endpoint
      if (response.status === 401) {
        console.debug(`Okta endpoint ${endpoint} returned 401, trying alternative endpoint`);
      } else {
        // Other errors: try next endpoint
        console.debug(`Okta endpoint ${endpoint} returned ${response.status}, trying alternative endpoint`);
      }
    } catch (err: any) {
      // Network/CORS error (TypeError with 'fetch' error)
      const isCorsOrNetworkError = err instanceof TypeError && (
        err.message.includes('CORS') ||
        err.message.includes('Failed to fetch') ||
        err.message.includes('NetworkError')
      );

      // Capture first error if not already captured
      if (!firstError) {
        firstError = {
          status: isCorsOrNetworkError ? 'CORS/Network' : 'Unknown',
          endpoint: endpoint,
          message: err.message,
          isCorsOrNetworkError
        };
      }

      console.debug(`Okta endpoint ${endpoint} failed, trying alternative:`, err.message);
    }

    // If we get here, no successful response was returned, so surface the captured failure.
    console.error('All Okta complexity endpoints failed:', firstError);
    throw firstError;
  }

  /**
   * Tells Okta to logout
   * @param postLogoutRedirectUri URI from the Okta dashboard
   * @param state The state to return to. If any URL be sure to have it encoded before sending. The state is returned as a csv (state=csv)
   */
  async logout(postLogoutRedirectUri?: string, state: string[] = []): Promise<void> {
    if (environment.useNewLoginPage) {
      try {
        await this.oktaAuthClient.stop();
      } catch (e) {
        console.error('stop: ', e);
      }
      try {
        const opts = {
          revokeAccessToken: true,
          revokeRefreshToken: true,
          clearTokensBeforeRedirect: true,
        } as SignoutOptions;
        if (postLogoutRedirectUri) {
          opts.postLogoutRedirectUri = postLogoutRedirectUri;
        }
        if (state.length) {
          opts.state = state.join(',');
        }
        await this.oktaAuthClient.signOut(opts);
      } catch (e) {
        console.error('signOut: ', e);
      }
    } else {
      if (await this.isUserLoggedIn()) {
        await this.oktaAuthClient.revokeAccessToken();
        await this.oktaAuthClient.closeSession();
      }
    }
  }

  async signOutOfTenant(orgOrigin: string, fromUri: string, state: string[] = []): Promise<void> {
    const fromUriUrl = new URL(fromUri);
    if (state.length) {
      fromUriUrl.searchParams.set('state', state.join(','));
    }
    const signOutUrl = `${orgOrigin}/login/signout?fromURI=${encodeURIComponent(fromUriUrl.toString())}`;
    this.windowService.navigateTo(signOutUrl);
  }

  async closeSession(): Promise<void> {
    try {
      await this.oktaAuthClient.closeSession();
    } catch (e) {
      // Session may already be closed or expired
    }
  }
}

